import type { ReportCatalog, ReportQuery } from "@gettactic/api";

/** How a result is drawn. `auto` resolves from the query's shape. */
export type ReportForm =
  | "auto"
  | "trend"
  | "bars"
  | "share"
  | "multiples"
  | "stack"
  | "grid"
  | "calendar"
  | "gauge"
  | "table"
  | "map";
export type ResolvedReportForm = Exclude<ReportForm, "auto">;
/** The forms ResultChart draws itself; the rest have components of their own. */
export type ChartForm = Exclude<
  ResolvedReportForm,
  "table" | "map" | "multiples"
>;

/** How many breakdowns a form needs to have something to draw. */
const BREAKDOWNS_NEEDED: Record<ResolvedReportForm, number> = {
  trend: 0,
  bars: 1,
  share: 1,
  multiples: 1,
  stack: 2,
  grid: 2,
  calendar: 0,
  gauge: 0,
  table: 0,
  map: 0
};

/** Forms that read one dimension only: a second breakdown is dropped for them. */
const ONE_DIMENSION_FORMS = new Set<ReportForm>(["share", "multiples"]);

/** Dimensions worth grouping by when a form needs one, most readable first. */
const PREFERRED_BREAKDOWNS = [
  "office",
  "category",
  "team",
  "floor",
  "resource_type",
  "access_mode",
  "weekday",
  "status",
  "priority",
  "type"
];

/**
 * The query reshaped so a form has something to draw: bars, shares and
 * small multiples need one breakdown, a grid or stacked bars two. Missing
 * ones are added from the measure's dimensions (weekday makes the best
 * second axis); a one-dimension form drops a second breakdown. A calendar
 * reads day by day. A ratio's share is its two parts, so it needs no
 * breakdown. Trend, gauge and table take the query as it is.
 */
export const shapeForForm = (
  catalog: ReportCatalog,
  query: ReportQuery,
  form: ReportForm
): ReportQuery => {
  let shaped = query;
  if (form === "calendar" && shaped.period.grain !== "day") {
    shaped = { ...shaped, period: { ...shaped.period, grain: "day" } };
  }
  const measure = catalog.measures.find((m) => m.key === query.measure);
  const wanted =
    form === "auto"
      ? 0
      : form === "share" && measure?.parts
        ? 0
        : BREAKDOWNS_NEEDED[form];
  const breakdown = [...(shaped.breakdown ?? [])];
  if (form === "share" && measure?.parts && breakdown.length > 0) {
    // A ratio's share is its two parts: a breakdown has no place in it.
    return { ...shaped, breakdown: [] };
  }
  if (ONE_DIMENSION_FORMS.has(form) && breakdown.length > 1) {
    return { ...shaped, breakdown: [breakdown[0]] };
  }
  if (breakdown.length >= wanted) {
    return shaped;
  }
  const used = new Set([
    ...breakdown,
    ...(query.filters ?? []).map((f) => f.dimension)
  ]);
  const free = [...PREFERRED_BREAKDOWNS, ...(measure?.dimensions ?? [])].filter(
    (d, i, all) =>
      all.indexOf(d) === i && measure?.dimensions.includes(d) && !used.has(d)
  );
  while (breakdown.length < wanted && free.length > 0) {
    // A grid reads best with weekday across the top.
    const pick =
      breakdown.length === 1 && free.includes("weekday") ? "weekday" : free[0];
    breakdown.push(pick);
    free.splice(free.indexOf(pick), 1);
  }
  return breakdown.length === (shaped.breakdown ?? []).length
    ? shaped
    : { ...shaped, breakdown };
};

/**
 * The query shape picks the chart: a plain measure is a trend over time, one
 * breakdown is a ranked bar list, two breakdowns are a grid. An explicit form
 * wins, except where the shape can't support it (a grid or a stack needs two
 * breakdowns, bars one) — then it falls back to what the shape allows. A
 * share with no breakdown is a ratio's two parts; the chart says so when
 * the measure has none. A paired question's share is its two measures,
 * whatever the breakdown.
 */
export const pickForm = (
  query: Pick<ReportQuery, "breakdown">,
  form: ReportForm = "auto",
  isPaired = false
): ResolvedReportForm => {
  const breakdowns = query.breakdown?.length ?? 0;
  // A grid has no room for a second measure: a paired question keeps to
  // bars however many cuts it has.
  const auto: ResolvedReportForm =
    breakdowns === 0 ? "trend" : breakdowns === 1 || isPaired ? "bars" : "grid";

  switch (form) {
    case "table":
    case "trend":
    case "gauge":
      return form;
    case "map":
    case "calendar":
      // Neither draws a second measure: a pair falls back to the shape.
      return isPaired ? auto : form;
    case "bars":
      return breakdowns >= 1 ? "bars" : auto;
    case "share":
      return isPaired || breakdowns <= 1 ? form : auto;
    case "multiples":
      return breakdowns === 1 && !isPaired ? form : auto;
    case "stack":
    case "grid":
      return breakdowns === 2 && !isPaired ? form : auto;
    default:
      return auto;
  }
};

/** The chart to draw for a resolved form where only a chart fits (tiles, cards). */
export const chartFormOf = (form: ResolvedReportForm): ChartForm =>
  form === "table" || form === "map" || form === "multiples" ? "trend" : form;
